import { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import withBreakpoint from 'core/hocs/withBreakpoint';


function Scroller(props) {
  const {
    children,
    className,
    listClassName,
    scrollLeft,
    isMobile,
    dataQa,
    disabled,
  } = props;

  const scrollerEl = useRef(null);
  const [isDragging, setDrag] = useState(false);

  useEffect(() => {
    if (typeof scrollLeft !== 'number') return;
    scrollerEl.current.scrollLeft += scrollLeft;
  }, [scrollLeft]);

  useEffect(() => {
    const scroller = scrollerEl?.current;

    if (!scroller || isMobile || disabled) return () => null;

    const pos = { left: 0, x: 0 };

    const mouseMoveHandler = e => {
      setDrag(true);
      // How far the mouse has been moved
      const dx = e.clientX - pos.x;
      // Scroll the element
      scroller.scrollLeft = pos.left - dx;
    };

    const mouseUpHandler = () => {
      document.removeEventListener('mousemove', mouseMoveHandler);
      document.removeEventListener('mouseup', mouseUpHandler);
      setDrag(false);
    };

    const mouseDownHandler = e => {
      e.preventDefault();
      // The current scroll
      pos.left = scroller.scrollLeft;
      // Get the current mouse position
      pos.x = e.clientX;

      document.addEventListener('mousemove', mouseMoveHandler);
      document.addEventListener('mouseup', mouseUpHandler);
    };

    scroller.addEventListener('mousedown', mouseDownHandler);

    return () => {
      scroller.removeEventListener('mousedown', mouseDownHandler);
      document.removeEventListener('mousemove', mouseMoveHandler);
      document.removeEventListener('mouseup', mouseUpHandler);
    };
  }, [isMobile, disabled]);

  return (
    <div
      className={cx('scroller', className, isDragging && '_isDragging', disabled && '_disabled')}
      ref={scrollerEl}
      {...dataQa && { 'data-qa': dataQa }}
      {...isDragging && { unselectable: 'on', draggable: false }}
    >
      {/* Компонент используется в AMP версии, стили выносить не нужно */}
      <style jsx>{`
        .scroller
          width 100%
          position relative
          overflow-x scroll
          overflow-y hidden
          cursor grab
          margin -20px 0 -20px -20px
          padding 20px 0 20px 20px
          -ms-overflow-style none
          -webkit-overflow-scrolling touch
          scrollbar-width none
          &::-webkit-scrollbar
            display none

          &._isDragging
            cursor grabbing

          &._disabled
            overflow-x hidden
            cursor default

        .content
          display inline-flex
          vertical-align bottom
          min-width 100%

          ._isDragging &
            pointer-events none
      `}</style>
      <div className={cx('content', listClassName)}>
        {children}
      </div>
    </div>
  );
}

Scroller.propTypes = {
  /**
   * Класс для кастомизации скроллера
   */
  className: PropTypes.string,
  /**
   * Класс для кастомизации контента скроллера
   */
  listClassName: PropTypes.string,
  /**
   * Задает начальный скрол (scrollLeft для обертки)
   */
  scrollLeft: PropTypes.number,

  /** @ignore */
  isMobile: PropTypes.bool,

  /** @ignore */
  dataQa: PropTypes.string,

  /**
   * Позволяет заблокировать scroll и draggable, переведя его в режим `overflow: hidden`.
   *
   * Может быть полезно:
   * - если хотим заблокировать прокрутку скроллера с помощью стрелок клавиатуры
   * - при верстке скелетона
   *
   * Например:
   * В сторисах клик по карточке в скроллере открывает модальное окно - portal.
   * И в такой ситуации фокус остается и на скроллере и на drum внутри модалки.
   * Из-за чего нажатие на стрелках клавиатуры двигает оба блока.
   */
  disabled: PropTypes.bool,
};

const ScrollerComponentWithHOCs = withBreakpoint(Scroller);
ScrollerComponentWithHOCs.displayName = 'Scroller';

export { Scroller as StorybookComponent };
export default ScrollerComponentWithHOCs;
